import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DisplayForm from '../../components/common/DisplayForm';
import { DisplayFormData, registerDisplay } from '../../services/displayService';
import { useAuth } from '../../contexts/AuthContext';

interface RegisterDisplayParams {
  [key: string]: string | undefined;
  clientId?: string;
}

const RegisterDisplayPage: React.FC = () => {
  const { currentUser } = useAuth();
  const params = useParams<RegisterDisplayParams>();
  const clientId = params.clientId || ''; // Extract and ensure we have a string
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  // Add logging to help debug - moved before early returns to fix hooks rule
  useEffect(() => {
    console.log(`RegisterDisplayPage mounted with clientId from params: ${clientId}`);
  }, [clientId]);

  // Check if user has paid subscription tier for display management
  const userSubscriptionTier = currentUser?.subscriptionTier || 'free';
  const hasPaidSubscription = ['individual', 'group', 'organization', 'custom'].includes(userSubscriptionTier);

  // Check if clientId is missing - this means they didn't come from a QR code
  if (!clientId.trim()) {
    return (
      <div className="container my-4">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="card">
              <div className="card-body text-center py-5">
                <i className="fas fa-exclamation-triangle" style={{ fontSize: '4rem', color: '#dc3545' }}></i>
                <h2 className="mt-3 mb-3">Invalid Registration Access</h2>
                <p className="lead text-muted mb-4">
                  Display registration requires scanning a QR code from an unregistered display.
                </p>
                
                <div className="card bg-light">
                  <div className="card-body text-start">
                    <h5 className="card-title">
                      <i className="fas fa-qrcode me-2"></i>
                      How to Register a Display
                    </h5>
                    <ol className="mb-0">
                      <li className="mb-2">
                        <strong>Set up your display device:</strong> Open a web browser on your TV, tablet, or computer
                      </li>
                      <li className="mb-2">
                        <strong>Navigate to:</strong> <code>{window.location.origin}/display/view/</code>
                      </li>
                      <li className="mb-2">
                        <strong>QR code appears:</strong> Your unregistered display will show a QR code
                      </li>
                      <li className="mb-2">
                        <strong>Scan with mobile:</strong> Use your phone to scan the QR code
                      </li>
                      <li>
                        <strong>Complete registration:</strong> You'll be brought to this page with the proper setup
                      </li>
                    </ol>
                  </div>
                </div>
                
                <div className="mt-4">
                  <Link to="/my-displays" className="btn btn-primary me-2">
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to My Displays
                  </Link>
                  <Link to="/dashboard" className="btn btn-outline-secondary">
                    <i className="fas fa-house me-2"></i>
                    Dashboard
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If user doesn't have paid subscription, show upgrade message
  if (!hasPaidSubscription) {
    return (
      <div className="container my-4">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="card">
              <div className="card-body text-center py-5">
                <i className="fas fa-display" style={{ fontSize: '4rem', color: '#6c757d' }}></i>
                <h2 className="mt-3 mb-3">Display Registration</h2>
                <p className="lead text-muted mb-4">
                  Display registration is a premium feature that allows you to set up digital displays 
                  to showcase inspiring notes in public spaces.
                </p>
                <div className="alert alert-info mb-4">
                  <h5 className="alert-heading">
                    <i className="fas fa-info-circle me-2"></i>
                    Upgrade Required
                  </h5>
                  <p className="mb-0">
                    Your current subscription ({userSubscriptionTier}) does not include display registration. 
                    Please upgrade to <strong>Individual</strong> or higher to register displays.
                  </p>
                </div>
                <div className="d-flex justify-content-center gap-3">
                  <Link to="/contributor/donations" className="btn btn-primary">
                    <i className="fas fa-arrow-up-circle me-2"></i>
                    Upgrade Subscription
                  </Link>
                  <Link to="/dashboard" className="btn btn-outline-secondary">
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to Dashboard
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle display registration form submission
  const handleSubmit = async (formData: DisplayFormData) => {
    try {
      setError(null);
      console.log('Form submitted with data:', formData);
      
      // Ensure clientId from URL params is used if not in the form data
      const submissionData = {
        ...formData,
        clientId: formData.clientId || clientId
      };
      
      // Register the display
      const result = await registerDisplay(submissionData);
      
      // Redirect to display details page or displays list
      if (result._id) {
        navigate(`/my-displays/${result._id}`);
      } else {
        navigate('/my-displays');
      }
    } catch (err) {
      console.error('Error registering display:', err);
      setError(
        err instanceof Error 
          ? err.message 
          : 'Failed to register display. Please try again.'
      );
    }
  };

  return (
    <div className="container my-4">
      <div className="row">
        <div className="col-md-8 offset-md-2">
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          
          <DisplayForm 
            clientId={clientId} 
            onSubmit={handleSubmit} 
          />
          
          <div className="mt-4">
            <div className="card bg-light">
              <div className="card-body">
                <h5 className="card-title">About Display Registration</h5>
                <p className="card-text">
                  By registering this display, you'll be able to manage its configuration 
                  and control what notes appear on it. The display will automatically 
                  connect to your account once registration is complete.
                </p>
                <p className="card-text">
                  <strong>Note:</strong> Make sure the client ID matches the ID shown on the 
                  unregistered display screen. If you navigated here from the display's QR code, 
                  this value should be pre-filled correctly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterDisplayPage; 